/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';

// Modal opened from Home's "Unfilled shifts" alert. Lists every unfilled slot the manager
// has across the rolling 7-day window and lets them pick a person to fill each one.
// Picker shows employees grouped Available / Unavailable with the reason (already scheduled,
// over weekly cap, role mismatch). Filling inserts a row into shifts and refreshes the list.

// Period defaults — same constants Dashboard.js uses for new-shift starts/ends.
const PERIOD_DEFAULTS = {
  'Breakfast': { start: '06:00', end: '11:00' },
  'Lunch': { start: '11:00', end: '16:00' },
  'Dinner': { start: '16:00', end: '23:00' },
  'Late Night': { start: '22:00', end: '02:00' },
};

function calcHours(start, end) {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let s = sh + (sm || 0) / 60;
  let e = eh + (em || 0) / 60;
  if (e < s) e += 24;
  return Math.max(0, e - s);
}

// Roles the employee can work — employees.role is text and may be comma-separated. Lower-case
// trimmed for case-insensitive matching against the slot's role.
function employeeRoles(emp) {
  if (!emp?.role) return [];
  return String(emp.role).split(',').map(r => r.trim().toLowerCase()).filter(Boolean);
}

// Time overlap: returns true if [aStart, aEnd) and [bStart, bEnd) intersect. Times are
// "HH:MM" 24h strings; midnight-spanning shifts add 24h to end before comparing.
function shiftsOverlap(aStart, aEnd, bStart, bEnd) {
  const toMin = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); };
  let a1 = toMin(aStart), a2 = toMin(aEnd); if (a2 <= a1) a2 += 24 * 60;
  let b1 = toMin(bStart), b2 = toMin(bEnd); if (b2 <= b1) b2 += 24 * 60;
  return a1 < b2 && b1 < a2;
}

export default function UnfilledShiftsModal({ companyId, slots, onClose, onGoToSchedule }) {
  // Active state — slots get removed from the list as they're filled.
  const [openSlots, setOpenSlots] = useState(slots);
  const [selectedSlotKey, setSelectedSlotKey] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [allShifts, setAllShifts] = useState([]); // shifts for the rolling window — for conflict / weekly-hour math
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null); // employee id while assignment is in flight
  const [error, setError] = useState('');

  // Pull employees + the shifts that overlap the unfilled slots' dates so we can compute
  // availability without round-tripping per slot.
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      setLoading(true);
      const dates = Array.from(new Set(openSlots.map(s => s.date)));
      // Pull a wider window so weekly-hour totals are accurate (whole week of each unfilled date).
      const minDate = dates.reduce((m, d) => d < m ? d : m, dates[0] || '');
      const maxDate = dates.reduce((m, d) => d > m ? d : m, dates[0] || '');
      const widenStart = (() => { if (!minDate) return null; const d = new Date(minDate + 'T00:00:00'); d.setDate(d.getDate() - 6); return d.toISOString().split('T')[0]; })();
      const widenEnd = (() => { if (!maxDate) return null; const d = new Date(maxDate + 'T00:00:00'); d.setDate(d.getDate() + 6); return d.toISOString().split('T')[0]; })();
      const [eRes, sRes] = await Promise.all([
        supabase.from('employees').select('id, name, color, initials, role, max_hours_per_week, phone').eq('company_id', companyId).order('name'),
        widenStart ? supabase.from('shifts').select('employee_id, day, start_time, end_time, is_active').eq('company_id', companyId).gte('day', widenStart).lte('day', widenEnd).eq('is_active', true) : Promise.resolve({ data: [] }),
      ]);
      setEmployees(eRes.data || []);
      setAllShifts(sRes.data || []);
      setLoading(false);
    })();
  }, [companyId]);

  const selectedSlot = useMemo(() => openSlots.find(s => slotKey(s) === selectedSlotKey) || null, [openSlots, selectedSlotKey]);

  // Compute availability per employee for the currently selected slot.
  const employeeAvailability = useMemo(() => {
    if (!selectedSlot) return [];
    const periodTimes = PERIOD_DEFAULTS[selectedSlot.period] || { start: '09:00', end: '17:00' };
    const slotHours = calcHours(periodTimes.start, periodTimes.end);
    const slotRole = (selectedSlot.role || '').toLowerCase();
    return employees.map(emp => {
      const reasons = [];
      const roles = employeeRoles(emp);
      const roleMatch = !slotRole || roles.includes(slotRole);
      if (!roleMatch) reasons.push(`Role mismatch (${emp.role || '—'})`);
      // Conflicts: any active shift on this date that overlaps the slot's period times.
      const conflicts = allShifts.filter(s => String(s.employee_id) === String(emp.id) && s.day === selectedSlot.date && shiftsOverlap(s.start_time, s.end_time, periodTimes.start, periodTimes.end));
      if (conflicts.length > 0) {
        const c = conflicts[0];
        reasons.push(`Already on ${shortTime(c.start_time)}–${shortTime(c.end_time)}`);
      }
      // Weekly hour cap (uses the calendar week the slot falls in).
      if (emp.max_hours_per_week != null) {
        const weekDates = sameWeekDates(selectedSlot.date);
        const weekHours = allShifts
          .filter(s => String(s.employee_id) === String(emp.id) && weekDates.includes(s.day))
          .reduce((sum, s) => sum + calcHours(s.start_time, s.end_time), 0);
        const newTotal = weekHours + slotHours;
        if (newTotal > Number(emp.max_hours_per_week)) {
          reasons.push(`Would hit ${newTotal.toFixed(1)}h / ${emp.max_hours_per_week}h cap`);
        }
      }
      return { emp, reasons, roleMatch };
    }).sort((a, b) => {
      // Available first; within group, role-matched first; then by name.
      const aAvail = a.reasons.length === 0;
      const bAvail = b.reasons.length === 0;
      if (aAvail !== bAvail) return aAvail ? -1 : 1;
      if (a.roleMatch !== b.roleMatch) return a.roleMatch ? -1 : 1;
      return (a.emp.name || '').localeCompare(b.emp.name || '');
    });
  }, [selectedSlot, employees, allShifts]);

  async function assignEmployee(emp) {
    if (!selectedSlot) return;
    const periodTimes = PERIOD_DEFAULTS[selectedSlot.period] || { start: '09:00', end: '17:00' };
    setAssigning(emp.id);
    setError('');
    const { error: insErr } = await supabase.from('shifts').insert({
      company_id: companyId,
      employee_id: emp.id,
      day: selectedSlot.date,
      shift_name: selectedSlot.period,
      role: selectedSlot.role,
      start_time: periodTimes.start,
      end_time: periodTimes.end,
      is_active: true,
    });
    setAssigning(null);
    if (insErr) {
      setError(insErr.message);
      return;
    }
    // Decrement the slot's count locally; remove the slot once filled to zero.
    setOpenSlots(prev => prev.flatMap(s => {
      if (slotKey(s) !== selectedSlotKey) return [s];
      const remaining = (s.count || 1) - 1;
      return remaining > 0 ? [{ ...s, count: remaining }] : [];
    }));
    setSelectedSlotKey(null);
    // Also reflect the new shift in our local cache so subsequent availability checks see it.
    setAllShifts(prev => [...prev, {
      employee_id: emp.id, day: selectedSlot.date,
      start_time: periodTimes.start, end_time: periodTimes.end, is_active: true,
    }]);
  }

  // Group remaining open slots by date for the list view.
  const slotsByDate = useMemo(() => {
    const map = new Map();
    openSlots.forEach(s => {
      if (!map.has(s.date)) map.set(s.date, { date: s.date, label: s.label, isToday: s.isToday, items: [] });
      map.get(s.date).items.push(s);
    });
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [openSlots]);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={onClose}>
      <div className="bg-white w-full h-full md:h-auto md:max-w-3xl md:max-h-[90vh] md:rounded-2xl overflow-y-auto m-0 md:m-4" onClick={(e) => e.stopPropagation()} style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">{selectedSlot ? 'Fill this shift' : 'Unfilled shifts'}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {selectedSlot
                ? `${selectedSlot.period} · ${selectedSlot.role} · ${selectedSlot.label}`
                : `${openSlots.length === 0 ? 'All slots filled' : `${openSlots.reduce((s, x) => s + (x.count || 1), 0)} slot${openSlots.length === 1 ? '' : 's'} across ${slotsByDate.length} day${slotsByDate.length === 1 ? '' : 's'}`}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {selectedSlot && (
              <button onClick={() => setSelectedSlotKey(null)} className="text-xs text-blue-600 hover:underline">← Back</button>
            )}
            {onGoToSchedule && !selectedSlot && (
              <button onClick={onGoToSchedule} className="text-xs font-medium text-blue-600 hover:underline">Open Schedule →</button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg" aria-label="Close">×</button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
        ) : selectedSlot ? (
          <EmployeePicker
            availability={employeeAvailability}
            assigning={assigning}
            onPick={assignEmployee}
            error={error}
          />
        ) : (
          <SlotList
            slotsByDate={slotsByDate}
            onPickSlot={(s) => { setSelectedSlotKey(slotKey(s)); setError(''); }}
          />
        )}
      </div>
    </div>
  );
}

function SlotList({ slotsByDate, onPickSlot }) {
  if (slotsByDate.length === 0) {
    return <div className="p-10 text-center text-sm text-gray-400">All open slots have been filled — nice work.</div>;
  }
  return (
    <div className="p-4 space-y-4">
      {slotsByDate.map(group => (
        <div key={group.date}>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{group.label}{group.isToday ? ' (today)' : ''}</div>
          <div className="space-y-1.5">
            {group.items.map((s) => (
              <button
                key={slotKey(s)}
                onClick={() => onPickSlot(s)}
                className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900">{s.period} · {s.role}</div>
                  <div className="text-xs text-gray-500">need {s.count}</div>
                </div>
                <span className="text-xs font-medium text-blue-600">Fill →</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmployeePicker({ availability, assigning, onPick, error }) {
  const available = availability.filter(a => a.reasons.length === 0);
  const unavailable = availability.filter(a => a.reasons.length > 0);
  return (
    <div className="p-4 space-y-4">
      {error && (
        <div className="px-3 py-2 bg-red-50 border-l-[3px] border-red-500 rounded-r-md text-xs text-red-700">{error}</div>
      )}

      <div>
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Available ({available.length})</div>
        {available.length === 0 ? (
          <div className="text-xs text-gray-400 px-3 py-3 bg-gray-50 rounded-lg">Nobody is fully available — pick someone below if you can override.</div>
        ) : (
          <div className="space-y-1.5">
            {available.map(({ emp }) => (
              <EmployeeRow key={emp.id} emp={emp} status="available" assigning={assigning === emp.id} onPick={() => onPick(emp)} />
            ))}
          </div>
        )}
      </div>

      {unavailable.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Unavailable ({unavailable.length})</div>
          <div className="space-y-1.5">
            {unavailable.map(({ emp, reasons }) => (
              <EmployeeRow key={emp.id} emp={emp} status="unavailable" reasons={reasons} assigning={assigning === emp.id} onPick={() => onPick(emp)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmployeeRow({ emp, status, reasons = [], assigning, onPick }) {
  const initials = emp.initials || (emp.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <button
      onClick={onPick}
      disabled={assigning}
      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors disabled:opacity-50 ${status === 'available' ? 'bg-green-50 hover:bg-green-100' : 'bg-gray-50 hover:bg-gray-100'}`}
    >
      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0" style={{ background: emp.color || '#6B7280' }}>
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 truncate">{emp.name || 'Unknown'}</div>
        <div className={`text-xs truncate ${status === 'available' ? 'text-green-700' : 'text-amber-700'}`}>
          {status === 'available' ? 'Available' : reasons.join(' · ')}
        </div>
      </div>
      <span className={`text-xs font-medium flex-shrink-0 ${status === 'available' ? 'text-green-700' : 'text-gray-500'}`}>
        {assigning ? 'Saving…' : status === 'available' ? 'Assign →' : 'Override'}
      </span>
    </button>
  );
}

// Helpers
function slotKey(s) { return `${s.date}|${s.period}|${s.role}`; }
function shortTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 || 12;
  return m && m !== 0 ? `${h12}:${String(m).padStart(2, '0')}${ampm}` : `${h12}${ampm}`;
}
// Returns YYYY-MM-DD strings for the calendar week (Sun-Sat) containing `date`.
// Used purely for weekly-hours math, so the exact week-start day doesn't have to match the
// company's preference — the cap is "hours over any 7-day window centered around the slot".
function sameWeekDates(date) {
  const d = new Date(date + 'T00:00:00');
  const dow = d.getDay();
  const start = new Date(d);
  start.setDate(d.getDate() - dow);
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(start); x.setDate(start.getDate() + i);
    return x.toISOString().split('T')[0];
  });
}
